import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import AuthContext from './AuthContext';

const ClinicalSessionContext = createContext();

export const useClinicalSession = () => {
  const context = useContext(ClinicalSessionContext);
  if (!context) {
    throw new Error(
      'useClinicalSession must be used within ClinicalSessionProvider'
    );
  }
  return context;
};

// Helper function to create initial session data
const createInitialSessionData = () => ({
  user: null,
  userName: '',
  notes: '',
  startTime: null,
  endTime: null,
  duration: 0,
  sessionType: '',
  goals: [],
  behaviors: [],
  interventions: '',
  observations: '',
  nextSteps: '',
  // Form state fields
  clinicianId: '',
  clinicianName: '',
  clinicianNpi: '',
  studentId: '',
  studentName: '',
  date: '',
  time: null,
  sessionLength: '',
  customSessionLength: '',
  sessionAttended: 'Yes',
  setting: '',
  customSetting: '',
  groupSize: '',
  groupType: '',
  customGroupType: '',
  absentReason: '',
  customAbsentReason: '',
  sessionType: '',
  customSessionType: '',
  absentNotes: '',
  sessionSummary: '',
  observations: '',
  isBillable: false,
  cptCodes: [],
  sdpServiceCodes: [],
  iepServiceTherapies: [],
  nonBillableReason: '',
  selectedGoals: [],
  selectedQuickTracks: [],
  editAccess: [],
  viewOnlyAccess: [],
  goalEntryDrafts: {},
  quickTrackDrafts: {},
  lastSavedAt: null,
});

export const ClinicalSessionProvider = ({ children }) => {
  // Store multiple sessions in an object keyed by sessionId
  const [sessions, setSessions] = useState({});
  // Store per-session chip positions when users drag minimized chips
  // Structure: { [sessionId]: { x: number, y: number } }
  const [chipPositions, setChipPositions] = useState({});
  const [activeSessionId, setActiveSessionId] = useState(null);

  // Open a new session or reopen an existing one
  const openSession = useCallback((user, userName) => {
    const sessionId = uuidv4();

    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        id: sessionId,
        isOpen: true,
        isMinimized: false,
        sessionData: {
          ...createInitialSessionData(),
          user,
          userName,
          startTime: new Date(),
        },
      },
    }));

    setActiveSessionId(sessionId);
  }, []);

  // Open a session for editing with pre-filled data
  const openSessionForEdit = useCallback((user, userName, prefilledData) => {
    const sessionId = uuidv4();

    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        id: sessionId,
        isOpen: true,
        isMinimized: false,
        // Store original unedited data separately for reference
        originalData: prefilledData?.originalSessionData || null,
        sessionData: {
          ...createInitialSessionData(),
          ...prefilledData,
          user,
          userName,
          startTime: new Date(),
        },
      },
    }));

    setActiveSessionId(sessionId);
    return sessionId;
  }, []);

  // Close and remove a session
  const closeSession = useCallback((sessionId) => {
    setSessions((prev) => {
      const newSessions = { ...prev };
      delete newSessions[sessionId];

      // Update active session if needed
      setActiveSessionId((prevActiveId) => {
        if (prevActiveId === sessionId) {
          const remainingSessions = Object.keys(newSessions);
          return remainingSessions.length > 0 ? remainingSessions[0] : null;
        }
        return prevActiveId;
      });

      return newSessions;
    });

    // Clean up chip position
    setChipPositions((prev) => {
      if (!prev[sessionId]) return prev;
      const newPositions = { ...prev };
      delete newPositions[sessionId];
      return newPositions;
    });
  }, []);

  const minimizeSession = useCallback((sessionId) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        isOpen: false,
        isMinimized: true,
      },
    }));
  }, []);

  const setIsSaving = useCallback((sessionId, isSaving) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        isSaving: isSaving,
      },
    }));
  }, []);

  const maximizeSession = useCallback((sessionId) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        isOpen: true,
        isMinimized: false,
      },
    }));
    setActiveSessionId(sessionId);
    // When maximizing, forget stored position so it spawns back in bar when minimized again
    setChipPositions((prev) => {
      if (!prev[sessionId]) return prev;
      const newPositions = { ...prev };
      delete newPositions[sessionId];
      return newPositions;
    });
  }, []);

  // Update session data for a specific session
  const updateSessionData = useCallback((sessionId, data) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        sessionData: {
          ...prev[sessionId].sessionData,
          ...data,
        },
      },
    }));
  }, []);

  // Clear/reset a specific session's data
  const clearSessionData = useCallback((sessionId) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        sessionData: createInitialSessionData(),
      },
    }));
  }, []);

  const setGoalEntryDraft = useCallback((sessionId, goalId, draft) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        sessionData: {
          ...prev[sessionId].sessionData,
          goalEntryDrafts: {
            ...prev[sessionId].sessionData.goalEntryDrafts,
            [goalId]: draft,
          },
        },
      },
    }));
  }, []);

  const removeGoalEntryDraft = useCallback((sessionId, goalId) => {
    setSessions((prev) => {
      const currentDrafts = prev[sessionId]?.sessionData?.goalEntryDrafts || {};
      if (!currentDrafts[goalId]) {
        return prev;
      }

      const updatedDrafts = { ...currentDrafts };
      delete updatedDrafts[goalId];

      return {
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          sessionData: {
            ...prev[sessionId].sessionData,
            goalEntryDrafts: updatedDrafts,
          },
        },
      };
    });
  }, []);

  const setQuickTrackDraft = useCallback((sessionId, trackId, draft) => {
    setSessions((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        sessionData: {
          ...prev[sessionId].sessionData,
          quickTrackDrafts: {
            ...prev[sessionId].sessionData.quickTrackDrafts,
            [trackId]: draft,
          },
        },
      },
    }));
  }, []);

  const removeQuickTrackDraft = useCallback((sessionId, trackId) => {
    setSessions((prev) => {
      const currentDrafts =
        prev[sessionId]?.sessionData?.quickTrackDrafts || {};
      if (!currentDrafts[trackId]) {
        return prev;
      }

      const updatedDrafts = { ...currentDrafts };
      delete updatedDrafts[trackId];

      return {
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          sessionData: {
            ...prev[sessionId].sessionData,
            quickTrackDrafts: updatedDrafts,
          },
        },
      };
    });
  }, []);

  // Persist chip position after drag stops
  const setChipPosition = useCallback((sessionId, position) => {
    setChipPositions((prev) => ({ ...prev, [sessionId]: position }));
  }, []);

  // Get a specific session by ID
  const getSession = useCallback(
    (sessionId) => {
      return sessions[sessionId] || null;
    },
    [sessions]
  );

  // Get all sessions
  const getAllSessions = useCallback(() => {
    return Object.values(sessions);
  }, [sessions]);

  // Get all minimized sessions
  const getMinimizedSessions = useCallback(() => {
    return Object.values(sessions).filter((session) => session.isMinimized);
  }, [sessions]);

  // Clear all sessions
  const clearAllSessions = useCallback(() => {
    setSessions({});
    setActiveSessionId(null);
    setChipPositions({});
  }, []);

  // Get user from AuthContext to clear sessions on logout
  const { user } = useContext(AuthContext);

  // Clear all sessions when user logs out
  useEffect(() => {
    if (!user) {
      clearAllSessions();
    }
  }, [user, clearAllSessions]);

  const value = {
    sessions,
    activeSessionId,
    openSession,
    openSessionForEdit,
    closeSession,
    minimizeSession,
    maximizeSession,
    updateSessionData,
    clearSessionData,
    setGoalEntryDraft,
    removeGoalEntryDraft,
    setQuickTrackDraft,
    removeQuickTrackDraft,
    getSession,
    getAllSessions,
    getMinimizedSessions,
    chipPositions,
    setChipPosition,
    clearAllSessions,
    setIsSaving,
  };

  return (
    <ClinicalSessionContext.Provider value={value}>
      {children}
    </ClinicalSessionContext.Provider>
  );
};
